package gr.tech.pdfgenerator.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.Resize
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.pdfgenerator.infrastructure.data.GeneratorData
import gr.tech.pdfgenerator.models.MSADeliverableDTO
import gr.tech.pdfgenerator.styles.*
import gr.tech.pdfgenerator.util.CONSTANTS.FONT_FAMILY
import gr.tech.pdfgenerator.util.INPUTS
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLTextAreaElement

@Composable
fun msaInputsSection() {
    var msaInputsState by GeneratorData.msaInputsState
    var showMSASample by remember { mutableStateOf(false) }

    Column(
        modifier = Modifier.width(875.px).padding(bottom = 3.cssRem),
        horizontalAlignment = Alignment.Start
    ) {
        Row(
            modifier = Modifier.margin(bottom = 12.px),
            verticalAlignment = Alignment.CenterVertically
        ) {
            CheckboxInput(
                checked = msaInputsState.addressOverwriteCheck,
                attrs = Modifier
                    .width(15.px)
                    .height(15.px)
                    .margin(right = 10.px)
                    .onClick {
                        msaInputsState = msaInputsState.copy(
                            addressOverwriteCheck = !msaInputsState.addressOverwriteCheck
                        )
                    }
                    .toAttrs()
            )
            SpanText(
                text = "Overwrite Company Address (Will be displayed instead)",
                modifier = Modifier
                    .fontSize(1.cssRem)
                    .fontWeight(FontWeight.Light)
                    .fontFamily(FONT_FAMILY)
            )
        }

        if (msaInputsState.addressOverwriteCheck) {
            Row(modifier = Modifier.gap(6.px).fillMaxWidth()) {
                StringInputLabel(
                    label = "State",
                    id = INPUTS.MSA.msaStateOverwrite,
                    initialValue = msaInputsState.stateOverwrite,
                ) { msaInputsState = msaInputsState.copy(stateOverwrite = it) }
                StringInputLabel(
                    label = "Address",
                    id = INPUTS.MSA.msaAddressOverwrite,
                    initialValue = msaInputsState.addressOverwrite,
                ) { msaInputsState = msaInputsState.copy(addressOverwrite = it) }
                StringInputLabel(
                    label = "City",
                    id = INPUTS.MSA.msaCityOverwrite,
                    initialValue = msaInputsState.cityOverwrite,
                ) { msaInputsState = msaInputsState.copy(cityOverwrite = it) }
                StringInputLabel(
                    label = "Zip",
                    id = INPUTS.MSA.msaZipOverwrite,
                    initialValue = msaInputsState.zipOverwrite,
                ) { msaInputsState = msaInputsState.copy(zipOverwrite = it) }
            }
        }

        StringInputLabel(
            label = "Utility/Program",
            id = INPUTS.MSA.msaRebateProgramInput,
            initialValue = msaInputsState.utilityProgram
        ) { msaInputsState = msaInputsState.copy(utilityProgram = it) }

        StringInputLabel(
            label = "Utility Representative",
            id = INPUTS.MSA.msaAuthorizerNameInput,
            initialValue = msaInputsState.authorizerName
        ) { msaInputsState = msaInputsState.copy(authorizerName = it) }

        TextAreaInputLabel(
            label = "SOW Project Description",
            id = INPUTS.MSA.msaProjectDescriptionInput,
            initialValue = msaInputsState.projectDescription
        ) {
            if (it.isBlank()) {
                msaInputsState = msaInputsState.copy(projectDescription = INPUTS.MSA.MsaProjectDefaultDescription)
                (document.getElementById(INPUTS.MSA.msaProjectDescriptionInput) as HTMLTextAreaElement).value =
                    INPUTS.MSA.MsaProjectDefaultDescription
            } else {
                msaInputsState = msaInputsState.copy(projectDescription = it)
            }
        }

        HorizontalDivider()

        H3(attrs = MSAInputTitleStyle.toAttrs()) { Text("Efficiency Measures") }

        EfficiencyMeasureTableInput(
            measures = msaInputsState.efficiencyMeasureList,
            onUpdate = { msaInputsState = msaInputsState.copy(efficiencyMeasureList = it) }
        )

        HorizontalDivider()

        H3(attrs = MSAInputTitleStyle.toAttrs()) { Text("Deliverables") }

        DeliverableTableInput(
            deliverables = msaInputsState.deliverableList,
            onUpdate = { msaInputsState = msaInputsState.copy(deliverableList = it) },
            showToggleSample = showMSASample,
            onToggleShowSample = { toggle -> showMSASample = toggle }
        )

        if (showMSASample) {
            H3(attrs = MSAInputTitleStyle.toModifier().toAttrs()) { Text("MSA SAMPLE DELIVERABLES") }

            MSASampleTextTable()
        }

        HorizontalDivider()
    }
}

@Composable
fun EfficiencyMeasureTableInput(
    measures: List<String>,
    onUpdate: (List<String>) -> Unit
) {
    var draggedIndex by remember { mutableStateOf(-1) }
    var dragOverIndex by remember { mutableStateOf(-1) }

    Table(attrs = MSADeliverableTableStyle.toAttrs()) {
        Tr {
            Th(MSATableControlCellStyle.toAttrs()) {}
            Th(MSADeliverableTextPreview.toModifier().padding(4.px).toAttrs()) {
                Text("Efficiency Measure")
            }
            Th(MSATableControlCellStyle.toAttrs()) {}
        }

        measures.forEachIndexed { index, measure ->
            Tr(
                attrs = MSADeliverableRowPreview.toModifier()
                    .then(
                        if (dragOverIndex == index && dragOverIndex != draggedIndex)
                            Modifier.borderTop(3.px, LineStyle.Solid, Theme.MediumGreen.rgb)
                        else Modifier
                    )
                    .onDragOver { it.preventDefault(); dragOverIndex = index }
                    .onDragLeave { if (dragOverIndex == index) dragOverIndex = -1 }
                    .onDrop {
                        if (draggedIndex != -1 && draggedIndex != dragOverIndex) {
                            val list = measures.toMutableList()
                            val item = list.removeAt(draggedIndex)
                            list.add(
                                if (dragOverIndex > draggedIndex) dragOverIndex - 1 else dragOverIndex,
                                item
                            )
                            onUpdate(list)
                        }
                        draggedIndex = -1
                        dragOverIndex = -1
                    }
                    .toAttrs()
            ) {
                Td(
                    attrs = MSATableControlCellStyle.toModifier()
                        .draggable(true)
                        .onDragStart { draggedIndex = index }
                        .toAttrs()
                ) {
                    FaGripVertical(modifier = MSATableGripStyle.toModifier())
                }
                Td(MSADeliverableColumnPreview.toModifier().padding(0.px).toAttrs()) {
                    TextArea(
                        value = measure,
                        attrs = stringInputStyle.toModifier().noBorder().resize(Resize.None)
                            .overflow(Overflow.Hidden).toAttrs {
                                ref { el ->
                                    el.style.height = "auto"
                                    el.style.height = "${el.scrollHeight}px"
                                    onDispose {}
                                }
                                onInput { event ->
                                    event.target.style.height = "auto"
                                    event.target.style.height = "${event.target.scrollHeight}px"
                                    val list = measures.toMutableList()
                                    list[index] = event.value
                                    onUpdate(list)
                                }
                            }
                    )
                }
                Td(MSATableControlCellStyle.toAttrs()) {
                    Button(
                        attrs = MSATableDeleteButtonStyle.toModifier()
                            .onClick {
                                val list = measures.toMutableList()
                                list.removeAt(index)
                                onUpdate(list)
                            }
                            .toAttrs()
                    ) {
                        FaTrash(size = IconSize.XS)
                    }
                }
            }
        }
    }

    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.Start
    ) {
        if (measures.size < 8) {
            Button(
                attrs = MSAAddRowButtonStyle.toModifier()
                    .onClick { onUpdate(measures + "") }
                    .toAttrs()
            ) {
                FaPlus(modifier = Modifier.margin(right = 6.px))
                Text("Add Measure")
            }
        }

        SpanText(
            text = "Total: ${measures.size} / 8",
            modifier = Modifier
                .fontFamily(FONT_FAMILY)
                .fontSize(1.cssRem)
                .fontWeight(FontWeight.Bold)
                .margin(left = 8.px)
        )
    }
}

@Composable
private fun DeliverableTableInput(
    deliverables: List<MSADeliverableDTO>,
    onUpdate: (List<MSADeliverableDTO>) -> Unit,
    showToggleSample: Boolean,
    onToggleShowSample: (Boolean) -> Unit,
) {
    var draggedIndex by remember { mutableStateOf(-1) }
    var dragOverIndex by remember { mutableStateOf(-1) }

    Table(attrs = MSADeliverableTableStyle.toAttrs()) {
        Tr {
            Th(MSATableControlCellStyle.toAttrs()) {}
            Th(MSADeliverableTextPreview.toModifier().padding(4.px).width(75.px).toAttrs()) { Text("Phase") }
            Th(MSADeliverableTextPreview.toModifier().padding(4.px).minWidth(150.px).toAttrs()) { Text("Deliverable") }
            Th(MSADeliverableTextPreview.toModifier().padding(4.px).minWidth(250.px).toAttrs()) { Text("Description") }
            Th(MSADeliverableTextPreview.toModifier().padding(4.px).minWidth(100.px).toAttrs()) { Text("Due Date") }
            Th(MSATableControlCellStyle.toAttrs()) {}
        }

        deliverables.forEachIndexed { index, deliverable ->
            Tr(
                attrs = MSADeliverableRowPreview.toModifier()
                    .then(
                        if (dragOverIndex == index && dragOverIndex != draggedIndex)
                            Modifier.borderTop(3.px, LineStyle.Solid, Theme.MediumGreen.rgb)
                        else Modifier
                    )
                    .onDragOver { it.preventDefault(); dragOverIndex = index }
                    .onDragLeave { if (dragOverIndex == index) dragOverIndex = -1 }
                    .onDrop {
                        if (draggedIndex != -1 && draggedIndex != dragOverIndex) {
                            val list = deliverables.toMutableList()
                            val item = list.removeAt(draggedIndex)
                            list.add(
                                if (dragOverIndex > draggedIndex) dragOverIndex - 1 else dragOverIndex,
                                item
                            )
                            onUpdate(list)
                        }
                        draggedIndex = -1
                        dragOverIndex = -1
                    }
                    .toAttrs()
            ) {
                Td(
                    attrs = MSATableControlCellStyle.toModifier()
                        .draggable(true)
                        .onDragStart { draggedIndex = index }
                        .toAttrs()
                ) {
                    FaGripVertical(modifier = MSATableGripStyle.toModifier())
                }
                Td(MSADeliverableColumnPreview.toModifier().minWidth(75.px).padding(0.px).toAttrs()) {
                    TextArea(
                        value = deliverable.phase,
                        attrs = stringInputStyle.toModifier().noBorder().resize(Resize.None)
                            .overflow(Overflow.Hidden).toAttrs {
                                ref { el ->
                                    el.style.height = "auto"
                                    el.style.height = "${el.scrollHeight}px"
                                    onDispose {}
                                }
                                onInput { event ->
                                    event.target.style.height = "auto"
                                    event.target.style.height = "${event.target.scrollHeight}px"
                                    val list = deliverables.toMutableList()
                                    list[index] = list[index].copy(phase = event.value)
                                    onUpdate(list)
                                }
                            }
                    )
                }
                Td(MSADeliverableColumnPreview.toModifier().minWidth(150.px).padding(0.px).toAttrs()) {
                    TextArea(
                        value = deliverable.deliverable,
                        attrs = stringInputStyle.toModifier().noBorder().resize(Resize.None)
                            .overflow(Overflow.Hidden).toAttrs {
                                ref { el ->
                                    el.style.height = "auto"
                                    el.style.height = "${el.scrollHeight}px"
                                    onDispose {}
                                }
                                onInput { event ->
                                    event.target.style.height = "auto"
                                    event.target.style.height = "${event.target.scrollHeight}px"
                                    val list = deliverables.toMutableList()
                                    list[index] = list[index].copy(deliverable = event.value)
                                    onUpdate(list)
                                }
                            }
                    )
                }
                Td(MSADeliverableColumnPreview.toModifier().minWidth(250.px).padding(0.px).toAttrs()) {
                    TextArea(
                        value = deliverable.description,
                        attrs = stringInputStyle.toModifier().noBorder().resize(Resize.None)
                            .overflow(Overflow.Hidden).toAttrs {
                                ref { el ->
                                    el.style.height = "auto"
                                    el.style.height = "${el.scrollHeight}px"
                                    onDispose {}
                                }
                                onInput { event ->
                                    event.target.style.height = "auto"
                                    event.target.style.height = "${event.target.scrollHeight}px"
                                    val list = deliverables.toMutableList()
                                    list[index] = list[index].copy(description = event.value)
                                    onUpdate(list)
                                }
                            }
                    )
                }
                Td(MSADeliverableColumnPreview.toModifier().minWidth(100.px).padding(0.px).toAttrs()) {
                    val dueDateId = "del-duedate-$index"
                    SideEffect {
                        (document.getElementById(dueDateId) as? HTMLInputElement)?.let {
                            if (it.value != deliverable.dueDate) it.value = deliverable.dueDate
                        }
                    }
                    Input(
                        type = InputType.Date,
                        attrs = stringInputStyle.toModifier().noBorder().id(dueDateId).toAttrs {
                            onInput { event ->
                                val list = deliverables.toMutableList()
                                list[index] = list[index].copy(dueDate = event.value)
                                onUpdate(list)
                            }
                        }
                    )
                }
                Td(MSATableControlCellStyle.toAttrs()) {
                    Button(
                        attrs = MSATableDeleteButtonStyle.toModifier()
                            .onClick {
                                val list = deliverables.toMutableList()
                                list.removeAt(index)
                                onUpdate(list)
                            }
                            .toAttrs()
                    ) {
                        FaTrash(size = IconSize.XS)
                    }
                }
            }
        }
    }

    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.Start
    ) {
        if (deliverables.size < 8) {
            Button(
                attrs = MSAAddRowButtonStyle.toModifier()
                    .onClick { onUpdate(deliverables + MSADeliverableDTO()) }
                    .toAttrs()
            ) {
                FaPlus(modifier = Modifier.margin(right = 6.px))
                Text("Add Deliverable")
            }
        }

        SpanText(
            text = "Total: ${deliverables.size} / 8",
            modifier = Modifier
                .fontFamily(FONT_FAMILY)
                .fontSize(1.cssRem)
                .fontWeight(FontWeight.Bold)
                .margin(left = 8.px)
        )

        Spacer()

        Button(
            attrs = MSAAddRowButtonStyle.toModifier()
                .onClick { onToggleShowSample(!showToggleSample) }
                .toAttrs()
        ) {
            if (showToggleSample) {
                FaEye(modifier = Modifier.margin(right = 6.px))
            } else {
                FaEyeSlash(modifier = Modifier.margin(right = 6.px))
            }
            Text("Toggle MSA Samples")
        }

    }
}

@Composable
fun MSASampleTextTable() {
    Table(attrs = MSADeliverableTableStyle.toModifier().margin(bottom = 1.cssRem).toAttrs {}) {
        MSASampleDeliverableRow("1", "SOW", "Proposed work scope and terms submitted to Customer.")
        MSASampleDeliverableRow("2", "Down Payment", "Customer will make down payment in the amount of $3,467 per unit, to Lighthouse Wholesale LLC prior to shipment of efficiency measures.")
        MSASampleDeliverableRow("3", "6 QTY Quest 335 Dehumidifier", "Company will procure and ship agreed equipment to Customer within 2 weeks from signed MSA.")
    }
}

@Composable
fun MSASampleDeliverableRow(
    value1: String = "",
    value2: String = "",
    value3: String = "",
    value4: String = ""
) {
    Tr(attrs = MSASampleDeliverableRowPreview.toModifier().toAttrs()) {
        Td(
            attrs = MSASampleDeliverableColumnPreview.toModifier()
                .then(MSASampleDeliverableTextPreview.toModifier())
                .minWidth(75.px).maxWidth(75.px).toAttrs()
        ) { Text(value1) }
        Td(
            attrs = MSASampleDeliverableColumnPreview.toModifier()
                .then(MSASampleDeliverableTextPreview.toModifier())
                .minWidth(150.px).maxWidth(150.px).toAttrs()
        ) { Text(value2) }
        Td(
            attrs = MSASampleDeliverableColumnPreview.toModifier()
                .then(MSASampleDeliverableTextPreview.toModifier())
                .minWidth(250.px).maxWidth(250.px).toAttrs()
        ) { Text(value3) }
        Td(
            attrs = MSASampleDeliverableColumnPreview.toModifier()
                .then(MSASampleDeliverableTextPreview.toModifier())
                .minWidth(100.px).maxWidth(100.px).toAttrs()
        ) { Text(value4) }
    }
}
